import React, { useState } from 'react';
import { Card, Text, Badge, InlineStack, BlockStack, Popover, ActionList } from '@shopify/polaris';
import { interactionTypeGroups } from './InteractionForm';
import { patchRequest } from '../../../shared/components/functions';

export default function InteractionCard({ interaction, statuses = [], onClick, onStatusChange }) {
  const [statusPopoverActive, setStatusPopoverActive] = useState(false);

  const currentStatus = statuses.find((s) => s.id === interaction.interaction_status_id);

  const handleStatusClick = (e) => {
    e.stopPropagation();
    e.preventDefault();
    setStatusPopoverActive(true);
  };

  const handleStatusSelect = async (e, statusId) => {
    if (e) {
      e.stopPropagation();
      e.preventDefault();
    }
    setStatusPopoverActive(false);
    try {
      await patchRequest(`/api/interactions/${interaction.id}/status`, {
        interaction_status_id: statusId
      });
      onStatusChange?.(interaction.id, statusId);
    } catch (error) {
      console.error('Error updating status:', error);
    }
  };

  const handlePopoverClose = () => {
    setStatusPopoverActive(false);
  };
  // Count attached documents
  let docCount = 0;
  if (interaction.S3ObjectKeys) {
    try {
      const keys = JSON.parse(interaction.S3ObjectKeys);
      docCount = Array.isArray(keys) ? keys.length : 0;
    } catch (e) {
      // Invalid JSON, ignore
    }
  }

  const getInteractionBadgeColor = (type) => {
    for (const group of Object.values(interactionTypeGroups)) {
      if (group.types.includes(type)) {
        return group.color;
      }
    }
    return 'default';
  };

  // Format date
  const formattedDate = interaction.event_date ? String(interaction.event_date).replace('T', ' ').slice(0, 10) : '-';

  // Check follow-up status
  const today = new Date().toISOString().split('T')[0];
  const followUpDate = interaction.follow_up_date ? String(interaction.follow_up_date).split('T')[0] : null;
  const isOverdue = followUpDate && followUpDate < today;
  const isDueToday = followUpDate && followUpDate === today;

  const handleCardClick = (e) => {
    // Don't trigger if clicking on popover or status area
    if (statusPopoverActive) return;
    onClick?.();
  };

  return (
    <div
      onClick={handleCardClick}
      style={{
        cursor: 'pointer',
        borderRadius: '8px',
        background: 'var(--p-color-bg-surface)',
        border: '1px solid var(--p-color-border)',
        borderLeft: isOverdue ? '3px solid #dc2626' : isDueToday ? '3px solid #d97706' : '1px solid var(--p-color-border)',
        overflow: 'hidden'
      }}
    >
      <div style={{ padding: '10px' }}>
        <BlockStack gap="100">
          {/* Follow-up indicator - subtle */}
          {followUpDate && (
            <Text as="span" variant="bodySm" tone={isOverdue ? 'critical' : isDueToday ? 'caution' : 'subdued'}>
              {isOverdue ? '● ' : isDueToday ? '○ ' : ''}
              {followUpDate}
            </Text>
          )}

          {/* Title */}
          <Text as="p" variant="bodyMd" fontWeight="semibold">
            {interaction.title?.length > 50 ? `${interaction.title.slice(0, 50)}...` : interaction.title || 'No title'}
          </Text>

          {/* Company */}
          {interaction.NAAM && (
            <Text as="p" variant="bodySm" tone="subdued">
              {interaction.NAAM}
            </Text>
          )}

          {/* Footer: Date, Type badge, Documents */}
          <InlineStack gap="100" align="space-between" blockAlign="center" wrap={false}>
            <InlineStack gap="050" blockAlign="center">
              <Text as="span" variant="bodySm" tone="subdued">
                {formattedDate}
              </Text>
              <Badge tone={getInteractionBadgeColor(interaction.type)} size="small">
                {interaction.type || 'Unknown'}
              </Badge>
            </InlineStack>

            {docCount > 0 && (
              <Badge tone="success" size="small">
                📎 {docCount}
              </Badge>
            )}
          </InlineStack>

          {/* Status selector */}
          {statuses.length > 0 && (
            <div onClick={(e) => e.stopPropagation()}>
              <Popover
                active={statusPopoverActive}
                activator={
                  <div onClick={handleStatusClick} style={{ cursor: 'pointer', display: 'inline-block' }}>
                    {currentStatus ? (
                      <Badge tone={currentStatus.color} size="small">
                        {currentStatus.name}
                      </Badge>
                    ) : (
                      <Badge tone="default" size="small">
                        No status
                      </Badge>
                    )}
                  </div>
                }
                onClose={handlePopoverClose}
                autofocusTarget="first-node"
              >
                <ActionList
                  actionRole="menuitem"
                  items={[
                    {
                      content: 'No status',
                      onAction: (e) => handleStatusSelect(e, null),
                      active: !interaction.interaction_status_id
                    },
                    ...statuses.map((status) => ({
                      content: status.name,
                      prefix: (
                        <Badge tone={status.color} size="small">
                          &nbsp;
                        </Badge>
                      ),
                      onAction: (e) => handleStatusSelect(e, status.id),
                      active: status.id === interaction.interaction_status_id,
                      suffix: !status.is_kanban_visible ? (
                        <Text tone="subdued" variant="bodySm">
                          (hidden)
                        </Text>
                      ) : null
                    }))
                  ]}
                />
              </Popover>
            </div>
          )}
        </BlockStack>
      </div>
    </div>
  );
}
